import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Video, FileText, ArrowRight, Search, TrendingUp, Calendar } from 'lucide-react';
import { EnhancedScrollReveal } from '../common/EnhancedScrollReveal';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
const featuredArticle = {
  title: 'Guía Completa: Transición a Dieta Fresca',
  excerpt: 'Descubre cómo cambiar la alimentación de tu mascota de forma segura, evitando problemas digestivos y maximizando los beneficios desde el primer día.',
  category: 'Nutrición',
  author: 'Dr. Roberto Méndez',
  readTime: '8 min',
  image: 'https://images.unsplash.com/photo-1544568100-847a948585b9?auto=format&fit=crop&q=80&w=1200'
};
const articles = [{
  title: '5 Señales de que tu perro necesita estimulación mental',
  category: 'Comportamiento',
  readTime: '5 min',
  image: 'https://images.unsplash.com/photo-1583337130417-3346a1be7dee?auto=format&fit=crop&q=80&w=600'
}, {
  title: 'Entendiendo las etiquetas: ¿Qué es realmente la "harina de carne"?',
  category: 'Educación',
  readTime: '6 min',
  image: 'https://images.unsplash.com/photo-1518914781460-a3daa46ce274?auto=format&fit=crop&q=80&w=600'
}, {
  title: 'Superalimentos de temporada para mascotas',
  category: 'Nutrición',
  readTime: '4 min',
  image: 'https://images.unsplash.com/photo-1606850780554-b55ea684fe84?auto=format&fit=crop&q=80&w=600'
}];
export function ResourcesHub() {
  return <section className="py-24 lg:py-32 bg-white" data-id="element-3211">
      <div className="container-custom max-w-7xl mx-auto" data-id="element-3212">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8" data-id="element-3213">
          <div className="max-w-2xl" data-id="element-3214">
            <EnhancedScrollReveal data-id="element-3215">
              <Badge variant="secondary" className="mb-6" data-id="element-3216">
                <BookOpen className="w-3 h-3 mr-1" data-id="element-3217" /> Academia Petiora
              </Badge>
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-charcoal-900 mb-4" data-id="element-3218">
                Aprende con expertos
              </h2>
              <p className="text-lg text-charcoal-600 font-light" data-id="element-3219">
                Recursos educativos gratuitos creados por veterinarios para
                dueños exigentes.
              </p>
            </EnhancedScrollReveal>
          </div>

          <EnhancedScrollReveal delay={0.2} data-id="element-3220">
            <div className="flex gap-4" data-id="element-3221">
              <div className="relative hidden lg:block" data-id="element-3222">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-3223" />
                <input type="text" placeholder="Buscar artículos..." className="pl-10 pr-4 py-3 rounded-full bg-gray-50 border border-gray-200 w-64 focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all" data-id="element-3224" />
              </div>
              <Button variant="outline" className="rounded-full" data-id="element-3225">
                Ver Todo el Blog <ArrowRight className="ml-2 w-4 h-4" data-id="element-3226" />
              </Button>
            </div>
          </EnhancedScrollReveal>
        </div>

        {/* Magazine Layout */}
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 mb-20" data-id="element-3227">
          {/* Featured Article (Left) */}
          <div className="lg:col-span-7" data-id="element-3228">
            <EnhancedScrollReveal className="h-full" data-id="element-3229">
              <div className="group relative h-full min-h-[500px] rounded-[2.5rem] overflow-hidden cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-500" data-id="element-3230">
                <img src={featuredArticle.image} alt={featuredArticle.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-3231" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" data-id="element-3232" />

                <div className="absolute bottom-0 left-0 p-8 md:p-12 text-white" data-id="element-3233">
                  <Badge className="bg-primary-500 text-white border-none mb-4" data-id="element-3234">
                    {featuredArticle.category}
                  </Badge>
                  <h3 className="text-3xl md:text-4xl font-serif font-bold mb-4 leading-tight group-hover:text-primary-200 transition-colors" data-id="element-3235">
                    {featuredArticle.title}
                  </h3>
                  <p className="text-gray-200 text-lg mb-6 line-clamp-2" data-id="element-3236">
                    {featuredArticle.excerpt}
                  </p>

                  <div className="flex items-center gap-6 text-sm font-medium text-gray-300" data-id="element-3237">
                    <div className="flex items-center gap-2" data-id="element-3238">
                      <div className="w-8 h-8 rounded-full bg-gray-700" data-id="element-3239" />
                      {featuredArticle.author}
                    </div>
                    <div className="flex items-center gap-2" data-id="element-3240">
                      <TrendingUp className="w-4 h-4" data-id="element-3241" />{' '}
                      {featuredArticle.readTime} de lectura
                    </div>
                  </div>
                </div>
              </div>
            </EnhancedScrollReveal>
          </div>

          {/* Sidebar List (Right) */}
          <div className="lg:col-span-5 flex flex-col gap-6" data-id="element-3242">
            {articles.map((article, index) => <EnhancedScrollReveal key={index} delay={index * 0.1} data-id="element-3243">
                <div className="group flex gap-6 p-4 rounded-3xl hover:bg-gray-50 transition-colors cursor-pointer border border-transparent hover:border-gray-100" data-id="element-3244">
                  <div className="w-32 h-32 rounded-2xl overflow-hidden shrink-0 shadow-sm" data-id="element-3245">
                    <img src={article.image} alt={article.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-3246" />
                  </div>
                  <div className="flex flex-col justify-center" data-id="element-3247">
                    <div className="text-xs font-bold text-primary-600 uppercase tracking-wider mb-2" data-id="element-3248">
                      {article.category}
                    </div>
                    <h4 className="text-lg font-bold text-charcoal-900 mb-2 leading-snug group-hover:text-primary-700 transition-colors" data-id="element-3249">
                      {article.title}
                    </h4>
                    <div className="flex items-center gap-2 text-xs text-gray-400 font-medium" data-id="element-3250">
                      <Calendar className="w-3 h-3" data-id="element-3251" /> {article.readTime}
                    </div>
                  </div>
                </div>
              </EnhancedScrollReveal>)}

            {/* Newsletter Mini-Card */}
            <EnhancedScrollReveal delay={0.4} className="mt-auto" data-id="element-3252">
              <div className="bg-charcoal-900 rounded-3xl p-8 text-white relative overflow-hidden" data-id="element-3253">
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary-500/20 rounded-full blur-2xl -translate-y-1/2 translate-x-1/3" data-id="element-3254" />
                <h4 className="text-xl font-bold font-serif mb-2" data-id="element-3255">
                  Únete a la manada
                </h4>
                <p className="text-gray-400 text-sm mb-6" data-id="element-3256">
                  Recibe consejos semanales y ofertas exclusivas.
                </p>
                <div className="flex gap-2" data-id="element-3257">
                  <input type="email" placeholder="Tu email" className="flex-1 bg-white/10 border border-white/10 rounded-full px-4 text-sm focus:outline-none focus:bg-white/20 transition-all" data-id="element-3258" />
                  <Button size="sm" variant="white" className="rounded-full" data-id="element-3259">
                    Suscribir
                  </Button>
                </div>
              </div>
            </EnhancedScrollReveal>
          </div>
        </div>
      </div>
    </section>;
}